import { useCallback, useEffect, useState } from 'react';
import { SLOT_LABEL, STATUS_LABEL, formatDateTime, formatDay } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from './util.js';

// Platform > Delete bookings (admins): remove a booking made by mistake or
// while testing. Only one nobody has been out to (Booked, Assigned or
// Cancelled). A copy, the reason and who did it stay in the audit trail.
export default function DeleteBookings() {
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const [recent, setRecent] = useState(null);
  const [deletable, setDeletable] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [target, setTarget] = useState(null);

  const loadRecent = useCallback(async () => {
    try {
      const r = await api('/booking-deletions');
      setRecent(r.deleted);
      setDeletable(r.deletable);
    } catch (e) {
      setError(problem(e));
    }
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const search = useCallback(async (term) => {
    setError(null);
    try {
      setFound((await api(`/bookings?q=${encodeURIComponent(term)}`)).bookings);
    } catch (e) {
      setError(problem(e));
    }
  }, []);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setFound(null); return undefined; }
    const t = setTimeout(() => search(term), 250);
    return () => clearTimeout(t);
  }, [q, search]);

  const done = async (ref) => {
    setTarget(null);
    setNotice({ tone: 'ok', text: `${ref} deleted. It's in the audit trail with your reason.` });
    await Promise.all([loadRecent(), q.trim().length >= 2 ? search(q.trim()) : null]);
  };

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Delete bookings</h1>
            <p className="muted">For bookings made by mistake or while testing. Anything else should be cancelled instead, which keeps it. Only a booking nobody has been out to can be deleted (Booked, Assigned or Cancelled). A copy, the reason and who deleted it stay in the audit trail.</p>
          </div>
        </div>
        <label className="fld">
          Find a booking
          <input value={q} onChange={(e) => { setQ(e.target.value); setNotice(null); }} placeholder="NU reference, client reference, customer, address or postcode" autoFocus />
        </label>
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
        {error && <p className="notice-line notice-bad">{error}</p>}
      </section>

      {found && (
        <section className="card">
          {!found.length ? <p className="muted">No bookings match.</p> : (
            <div className="table-scroll">
              <table className="table roster">
                <thead><tr><th>Ref</th><th>Address</th><th>Customer</th><th>Day</th><th>Team</th><th>Status</th><th aria-label="Delete" /></tr></thead>
                <tbody>
                  {found.map((b) => (
                    <tr key={b.id}>
                      <td data-label="Ref"><strong>{b.reference}</strong>{b.clientRef && <div className="small muted">{b.clientRef}</div>}</td>
                      <td data-label="Address">{[b.addressLine1, b.postcode].filter(Boolean).join(', ')}</td>
                      <td data-label="Customer">{b.customerName || '—'}</td>
                      <td data-label="Day">{b.bookingDate ? `${formatDay(b.bookingDate)}${b.slot ? ` · ${SLOT_LABEL[b.slot]}` : ''}` : '—'}</td>
                      <td data-label="Team">{b.teamName || '—'}</td>
                      <td data-label="Status"><span className={`status status-${b.status}`}>{STATUS_LABEL[b.status]}</span></td>
                      <td>
                        {deletable.includes(b.status)
                          ? <button type="button" className="btn danger-btn" onClick={() => setTarget(b)}>Delete</button>
                          : <span className="small muted">Visited, keep</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="card">
        <h2>Recently deleted</h2>
        {!recent && !error && <p className="muted">Loading…</p>}
        {recent && !recent.length && <p className="muted">Nothing deleted yet.</p>}
        {recent && recent.length > 0 && (
          <div className="table-scroll">
            <table className="table roster">
              <thead><tr><th>When</th><th>Booking</th><th>Reason</th><th>By</th></tr></thead>
              <tbody>
                {recent.map((d) => (
                  <tr key={d.bookingId}>
                    <td data-label="When">{formatDateTime(d.deletedAt)}</td>
                    <td data-label="Booking">{d.summary}</td>
                    <td data-label="Reason">{d.reason}</td>
                    <td data-label="By">{d.deletedByEmail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {target && <ConfirmDelete booking={target} onClose={() => setTarget(null)} onDone={done} />}
    </div>
  );
}

// Also used by the Bookings list (admins).
export function ConfirmDelete({ booking, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api(`/booking-deletions/${booking.id}`, { method: 'DELETE', body: { reason } });
      onDone(r.reference);
    } catch (err) {
      setError(problem(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={`Delete ${booking.reference}?`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Keep it</button>
          <button type="submit" form="delete-booking" className="btn danger-btn" disabled={busy || reason.trim().length < 3}>{busy ? 'Deleting…' : 'Delete booking'}</button>
        </>
      )}
    >
      <form id="delete-booking" className="stack" onSubmit={go}>
        <p>{[booking.addressLine1, booking.postcode].filter(Boolean).join(', ')}{booking.customerName ? ` · ${booking.customerName}` : ''}</p>
        <p className="small muted">It disappears from the calendar, the list and Plan the day. This can&apos;t be undone from the app. If the job might still happen, cancel it instead.</p>
        <label className="fld">
          Why is it being deleted?
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Test booking, Duplicate of NU-000012" maxLength={500} autoFocus />
        </label>
        {error && <p className="notice-line notice-bad">{error}</p>}
      </form>
    </Modal>
  );
}
